/**
 * Track Cell Items Page
 *
 * Shows all items belonging to a specific (Category × Altitude) track cell,
 * with the altitude questions for that category displayed in the right panel.
 *
 * Toggle: "Show alternate frames" — when ON, items that have a non-primary
 * frame matching this cell also appear (the same item can appear multiple
 * times, once per matching frame).
 *
 * Route: /prioritization/items/track-cell/:category/:level
 */

import { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Layers,
  RotateCcw,
  Users,
  Expand,
  ExternalLink,
  Inbox,
  Grid3x3,
  Repeat2,
} from 'lucide-react';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { PageHeader } from '../../components/prioritization/PageHeader';
import {
  ALTITUDE_NAMES_BY_CATEGORY,
  ALTITUDE_LEVEL_NAMES,
  ALTITUDE_LEVEL_DESCRIPTIONS,
  type AltitudeLevel,
} from '../../types/prioritization/itemLifecycle';
import { getAltitudeQuestionSet } from '../../lib/altitudeQuestions';
import { CATEGORY_BADGE, CATEGORY_BADGE_FALLBACK } from '../../lib/itemCategoryColors';
import type { ItemLanguageType, ItemFrame } from '../../types/prioritization/lifecycle';

// ============================================================================
// Types
// ============================================================================

/** A row in the list — either the item's primary classification, or one of its frames */
interface CellRow {
  key: string;
  itemId: string;
  title: string;
  description?: string;
  status: string;
  /** When set, this row represents a non-primary frame of the item */
  frame?: ItemFrame;
}

// ============================================================================
// Constants
// ============================================================================

const CATEGORY_LABELS: Record<string, string> = {
  problem: 'Problem',
  opportunity: 'Opportunity',
  risk: 'Risk',
  solution: 'Solution',
  commitment: 'Commitment',
  investment: 'Investment',
};

const ALTITUDE_COLORS: Record<number, { border: string; text: string; bg: string }> = {
  5: { border: 'border-[var(--coral)]',    text: 'text-[var(--coral)]',    bg: 'bg-[var(--coral-dim)]' },
  4: { border: 'border-[var(--mustard)]', text: 'text-[var(--mustard)]', bg: 'bg-[var(--mustard-dim)]' },
  3: { border: 'border-[var(--mustard)]',  text: 'text-[var(--mustard)]',  bg: 'bg-[var(--mustard-dim)]' },
  2: { border: 'border-[var(--slate)]',   text: 'text-[var(--text-sec)]',   bg: 'bg-[var(--slate-dim)]' },
  1: { border: 'border-[var(--rule)]',   text: 'text-[var(--text-sec)]',   bg: 'bg-[var(--surface)]' },
};

const DIMENSION_META = [
  { key: 'reversibility', icon: RotateCcw, label: 'Reversibility', iconColor: 'text-[var(--text-ter)]', iconBg: 'bg-[var(--slate-dim)]' },
  { key: 'coordination',  icon: Users,     label: 'Coordination',  iconColor: 'text-[var(--text-ter)]',   iconBg: 'bg-[var(--slate-dim)]' },
  { key: 'scope',         icon: Expand,    label: 'Scope',         iconColor: 'text-[var(--green)]', iconBg: 'bg-[var(--green-dim)]' },
] as const;

const STATUS_DOT: Record<string, string> = {
  active:   'bg-[var(--green)]',
  deferred: 'bg-[var(--mustard)]',
  archived: 'bg-[var(--rule-bright)]',
};

// ============================================================================
// Component
// ============================================================================

export function TrackCellItemsPage() {
  const { category, level } = useParams<{ category: string; level: string }>();
  const navigate = useNavigate();

  const [showAlternateFrames, setShowAlternateFrames] = useState(false);

  const altitudeLevel = level ? (Number(level) as AltitudeLevel) : null;
  const isValid =
    category &&
    CATEGORY_LABELS[category] &&
    altitudeLevel !== null &&
    altitudeLevel >= 1 &&
    altitudeLevel <= 5;

  const items = useItemsStore((s) => s.items);

  const rows = useMemo((): CellRow[] => {
    if (!isValid) return [];

    const result: CellRow[] = [];

    for (const item of items) {
      const exitFields = item as { exit_status?: string; exitStatus?: string };
      const status = exitFields.exit_status ?? exitFields.exitStatus ?? 'active';
      const primaryCatMatch = item.itemCategory === category;
      const primaryLvlMatch =
        item.altitudeLevel === altitudeLevel ||
        item.altitudeLevel === String(altitudeLevel) ||
        Number(item.altitudeLevel) === altitudeLevel;

      if (primaryCatMatch && primaryLvlMatch) {
        result.push({
          key: item.id,
          itemId: item.id,
          title: item.title ?? '',
          description: item.description ?? undefined,
          status,
        });
      } else if (showAlternateFrames && item.frames?.length) {
        // Check each non-primary frame for a match
        for (const frame of item.frames) {
          const frameCatMatch = frame.frameType === category;
          const frameLvlMatch = Number(frame.altitudeLevel) === altitudeLevel;
          if (frameCatMatch && frameLvlMatch) {
            result.push({
              key: `${item.id}::${frame.id}`,
              itemId: item.id,
              title: frame.title ?? item.title ?? '',
              description: frame.description ?? item.description ?? undefined,
              status,
              frame,
            });
          }
        }
      }
    }

    return result;
  }, [items, category, altitudeLevel, isValid, showAlternateFrames]);

  const primaryCount = rows.filter((r) => !r.frame).length;
  const frameCount = rows.filter((r) => !!r.frame).length;

  const questionSet = useMemo(() => {
    if (!category || !CATEGORY_LABELS[category]) return null;
    return getAltitudeQuestionSet(category as ItemLanguageType);
  }, [category]);

  if (!isValid) {
    return (
      <div className="p-6">
        <p className="text-sm text-[var(--text-ter)]">
          Invalid track cell.{' '}
          <Link to="/prioritization/documentation/track-cells" className="text-[var(--text-ter)] hover:underline">
            Browse all track cells →
          </Link>
        </p>
      </div>
    );
  }

  const catBadge = CATEGORY_BADGE[category] ?? CATEGORY_BADGE_FALLBACK;
  const ac = ALTITUDE_COLORS[altitudeLevel!];
  const cellName = ALTITUDE_NAMES_BY_CATEGORY[category]?.[altitudeLevel!] ?? `${CATEGORY_LABELS[category]} ${altitudeLevel}`;
  const levelName = ALTITUDE_LEVEL_NAMES[altitudeLevel!];
  const levelDesc = ALTITUDE_LEVEL_DESCRIPTIONS[altitudeLevel!];
  const categoryLabel = CATEGORY_LABELS[category];

  return (
    <div className="p-6 space-y-5">

      {/* Back link */}
      <div>
        <button
          onClick={() => navigate('/prioritization/documentation/track-cells')}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Comparison Cells
        </button>
      </div>

      <PageHeader
        icon={<Grid3x3 className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title={cellName}
        subtitle={`${categoryLabel} · Altitude ${altitudeLevel} · ${levelName} — ${levelDesc}`}
        actions={
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${catBadge.bg} ${catBadge.text}`}>
              {categoryLabel}
            </span>
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded border-2 ${ac.border} ${ac.text}`}>
              <Layers className="h-3 w-3" />
              {altitudeLevel}
            </span>
          </div>
        }
      />

      <div className="flex gap-6 items-start">

        {/* ── Item List ── */}
        <div className="flex-1 min-w-0">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">

            {/* List header */}
            <div className="px-4 py-3 border-b border-[var(--rule)] flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[var(--text-sec)]">
                  {primaryCount} item{primaryCount !== 1 ? 's' : ''}
                </span>
                {showAlternateFrames && frameCount > 0 && (
                  <span className="text-xs text-[var(--text-ter)]">
                    + {frameCount} additional {frameCount !== 1 ? 'categories' : 'category'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {/* Alternate frames toggle */}
                <button
                  onClick={() => setShowAlternateFrames((v) => !v)}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5  border transition-colors ${
                    showAlternateFrames
                      ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]'
                      : 'bg-[var(--bg-raised)] text-[var(--text-ter)] border-[var(--rule)] hover:border-[var(--rule)] hover:text-[var(--text-sec)]'
                  }`}
                  title="When on, items that have an additional category in this cell also appear — one row per category"
                >
                  <Repeat2 className="h-3.5 w-3.5" />
                  Additional categories
                </button>
                <Link
                  to={`/prioritization/items?category=${category}&altitude=${altitudeLevel}`}
                  className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
                >
                  Open in Items
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            </div>

            {rows.length === 0 ? (
              <div className="py-16 text-center">
                <Inbox className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-3" />
                <p className="text-sm font-medium text-[var(--text-ter)]">No items in this track cell</p>
                <p className="text-xs text-[var(--text-ter)] mt-1">
                  Items classified as <span className="font-medium">{categoryLabel}</span> at altitude{' '}
                  <span className="font-medium">{altitudeLevel}</span> will appear here.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-[var(--rule)]">
                {rows.map((row) => {
                  const dot = STATUS_DOT[row.status] ?? STATUS_DOT.active;
                  return (
                    <li key={row.key}>
                      <Link
                        to={`/prioritization/items/${row.itemId}`}
                        className="flex items-start gap-3 px-4 py-3 hover:bg-[var(--surface)] transition-colors group"
                      >
                        <div className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${dot}`} title={row.status} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-[var(--text-pri)] group-hover:text-[var(--text-ter)] truncate">
                              {row.title || <span className="italic text-[var(--text-ter)]">Untitled</span>}
                            </p>
                            {row.frame && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)] flex-shrink-0">
                                <Repeat2 className="h-2.5 w-2.5" />
                                Additional category
                              </span>
                            )}
                          </div>
                          {row.description && (
                            <p className="text-xs text-[var(--text-ter)] mt-0.5 line-clamp-2">{row.description}</p>
                          )}
                        </div>
                        <ExternalLink className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] flex-shrink-0 mt-1" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* ── Questions Panel ── */}
        {questionSet && (
          <div className="w-72 flex-shrink-0 space-y-4">

            {/* Cell identity card */}
            <div className={`bg-[var(--bg-raised)]  border ${ac.border} p-4`}>
              <p className="text-xs font-bold uppercase tracking-wide text-[var(--text-ter)] mb-2">This track cell</p>
              <h3 className="text-lg font-bold text-[var(--text-pri)]">{cellName}</h3>
              <p className={`text-xs font-semibold mt-1 ${ac.text}`}>Level {altitudeLevel} — {levelName}</p>
              <p className="text-xs text-[var(--text-ter)] mt-1.5">{levelDesc}</p>
            </div>

            {/* Questions */}
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 space-y-5">
              <p className="text-xs font-bold uppercase tracking-wide text-[var(--text-ter)]">How altitude is determined</p>

              {questionSet.questions.map((q, i) => {
                const dim = DIMENSION_META[i];
                const DimIcon = dim.icon;
                const matchedOption = q.options.find((o) => o.value === altitudeLevel);
                return (
                  <div key={q.id} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6  flex items-center justify-center flex-shrink-0 ${dim.iconBg}`}>
                        <DimIcon className={`h-3 w-3 ${dim.iconColor}`} />
                      </span>
                      <span className="text-xs font-semibold text-[var(--text-sec)]">{dim.label}</span>
                    </div>
                    <p className="text-xs text-[var(--text-ter)] pl-8">{q.question}</p>
                    {matchedOption && (
                      <div className="ml-8 px-2.5 py-2 bg-[var(--surface)] border border-[var(--rule)]">
                        <p className="text-xs font-semibold text-[var(--text-pri)]">{matchedOption.label}</p>
                        <p className="text-xs text-[var(--text-ter)] mt-0.5">{matchedOption.description}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TrackCellItemsPage;
